import {
  Equal,
  GreaterThan,
  GreaterThanOrEqual,
  INestedWhereCommand,
  IQueryCommandOptions,
  IWhereCommand,
  In,
  LessThan,
  Nested,
  Not,
  Null,
  Or,
  convertQueryCommandToQueryString,
  mergeQueryCommandOptions,
} from '@llws/dynamic-router-utils';
import {
  IPartnerInquiries,
  ISubscriptions,
  ISubscriptionsHasBuildings,
} from '@llws/lift-entity-interfaces';
import { IPromotedListing, IClientPromotedListingsBreakdown } from '@llws/platform-shared-types/types/api/syndication-api';
import dayjs from 'dayjs';
import { adminRequest } from '@/shared/request-configs/admin-request';
import { IAction, IStateStrict } from '@/shared/interfaces/store-base';
import { IApiResponse } from '@/shared';
import { downloadCSV } from '../../shared/helpers/download-csv';
import { IExportFilterPayloadV2 } from '../../shared/interfaces/export-filter-payload-v2';
import { IFilterPayloadV2 } from '../../shared/interfaces/filter-payload-v2';
import { StoreBaseImplementationV2 } from '../../shared/classes/store-base-implementation-v2';

// Base store configuration
const adminGatewayResourcePath: string = '/syndication-api/subscriptions';
const listViewName: string = 'promoted-listings';
const showLogging: boolean = false;

interface IPromotedListingTransferResponse {
  currentSubscription: ISubscriptions;
  insertedRecords: {
    subscriptions: ISubscriptions[];
    subscriptionsHasBuildings: ISubscriptionsHasBuildings[];
    partnerInquiries: IPartnerInquiries[];
  }
}

export type TPromotedListingStatusItem = 'active' | 'inactive' | 'requested';
export type TPromotedListingStatusItems = Array<TPromotedListingStatusItem>;

interface IStatusCommandMap {
  conditon: (items: TPromotedListingStatusItems) => Boolean;
  command: IWhereCommand | INestedWhereCommand;
}

const currentDate = dayjs().format('YYYY-MM-DD');

/**
 * Array of conditions/commands for generating
 * where clauses / commands based on selected Status filters.
 *
 * Various commands are required to filter subscription data
 * and related data depending on what status' are selected.
 */
const statusCommandMap: IStatusCommandMap[] = [
  // Active / Active + Requested
  {
    conditon: (items) => items.includes('active') && !items.includes('inactive') && !items.includes('requested'),
    command: {
      ...Nested([
        Or(Null('s.expiryDate')), // no expiry
        Or(GreaterThanOrEqual('s.expiryDate', currentDate)), // expiryDate in the future
        Not(Null('shb.activeDate')), // approved only, not requested
      ]),
      condition: 'and',
    },
  },
  // Active + Requested
  {
    conditon: (items) => items.includes('active') && !items.includes('inactive'),
    command: {
      ...Nested([
        Or(Null('s.expiryDate')), // no expiry
        Or(GreaterThanOrEqual('s.expiryDate', currentDate)), // expiryDate in the future
        Or(Null('shb.activeDate')),
      ]),
      condition: 'and',
    },
  },
  // Inactive
  {
    conditon: (items) => items.includes('inactive') && !items.includes('active') && !items.includes('requested'),
    command: {
      ...Nested([
        Or(LessThan('s.expiryDate', currentDate)), // expiryDate in the past
        Not(Null('shb.activeDate')), // approved only, not requested
      ]),
      condition: 'and',
    },
  },
  // Inactive + Requested
  {
    conditon: (items) => items.includes('inactive') && !items.includes('active'),
    command: {
      ...Nested([
        Or(LessThan('s.expiryDate', currentDate)), // expiryDate in the past
        Or(Null('shb.activeDate')), // requested
      ]),
      condition: 'and',
    },
  },
  // Not Requested, Only Approved
  {
    conditon: (items) => !items.includes('requested') && (items.includes('active') || items.includes('inactive')),
    command: Not(Null('shb.activeDate')), // approved only, not requested
  },
  // Requested Only ( ie. Not Approved )
  {
    conditon: (items) => items.includes('requested') && !items.includes('active') && !items.includes('inactive'),
    command: Null('shb.activeDate'), // not approved
  },
];

export const getPromotedListingsStatusWhereCommands = (statusItems: TPromotedListingStatusItems) => {
  const commands = statusCommandMap.filter((cmd) => cmd.conditon(statusItems)).map((cmd) => cmd.command);
  return commands;
};

/**
 * Get Filters for active Promoted Listings Contract subscriptions for a client.
 */
const defaultGetListFilters: IQueryCommandOptions = {};
const promotedListingGetListFilters: IQueryCommandOptions = {
  alias: 's',
  select: [
    'b.buildingName',
    'b.streetNumber',
    'b.streetName',
    'gc.cityName',
    'p.name',
    'p.partnerKey',
    'lplt.name',
    'lplt.tierLevel',
    'lplt.partnerKey',
    'lplt.description2',
    'lplt.feedLabel',
    's.id',
    's.startDate',
    's.minDuration',
    's.expiryDate',
    's.baseSubscriptionId',
    's.parentSubscriptionId',
    's.customConfiguration',
    'shb.activeDate',
    'rejectionCount',
    'transferRejectionCount',
    'cancellationRequested',
    'cancellationApproved',
    'transferRequested',
    'transferApproved',
    'upgradeRequested',
    'upgradeApproved',
    'downgradeRequested',
    'downgradeApproved',
  ],
  relations: [
    {
      path: 's.subscriptionsHasBuildings',
      alias: 'shb',
    },
    {
      path: 'shb.building',
      alias: 'b',
    },
    {
      path: 'b.city',
      alias: 'gc',
    },
    {
      path: 's.baseSubscription',
      alias: 'bs',
    },
    {
      path: 'bs.listingPromotedListingTypes',
      alias: 'lplt',
    },
    {
      path: 'bs.partner',
      alias: 'p',
    },
    {
      path: 'bs.service',
      alias: 'se',
    },
    {
      path: 'se.billingType',
      alias: 'bt',
    },
    {
      path: 's.partnerInquiries',
      alias: 'pi',
      whereCommands: [
        Equal('pi.inquiryType', 'cancellation'),
        In('pi.status', ['requested', 'approved']),
      ],
    },
    {
      path: 's.newPartnerInquiries',
      alias: 'npi',
      whereCommands: [
        In('npi.inquiryType', ['transfer', 'upgrade', 'downgrade']),
        In('npi.status', ['requested', 'approved']),
      ],
    },
  ],
  aggregations: [
    {
      table: 'partner_inquiries',
      propertyAlias: 'rejectionCount',
      column: 'id',
      aggregate: 'COUNT',
      relations: [{
        path: 'subscription',
        alias: 'pis',
      }],
      whereCommands: [
        Equal('inquiryType', 'promoted'),
        Equal('status', 'rejected'),
        Equal('subscriptionId', 's.id'),
      ],
    },
    {
      table: 'partner_inquiries',
      propertyAlias: 'transferRejectionCount',
      column: 'id',
      aggregate: 'COUNT',
      relations: [{
        path: 'subscription',
        alias: 'pis',
      }],
      whereCommands: [
        In('inquiryType', ['transfer', 'upgrade', 'downgrade']),
        Equal('status', 'rejected'),
        Equal('newSubscriptionId', 's.id'),
      ],
    },
    {
      table: 'partner_inquiries',
      propertyAlias: 'cancellationRequested',
      column: 'id',
      aggregate: 'COUNT',
      relations: [{
        path: 'subscription',
        alias: 'pis',
      }],
      whereCommands: [
        Equal('inquiryType', 'cancellation'),
        Equal('status', 'requested'),
        Equal('subscriptionId', 's.id'),
      ],
    },
    {
      table: 'partner_inquiries',
      propertyAlias: 'cancellationApproved',
      column: 'id',
      aggregate: 'COUNT',
      relations: [{
        path: 'subscription',
        alias: 'pis',
      }],
      whereCommands: [
        Equal('inquiryType', 'cancellation'),
        Equal('status', 'approved'),
        Equal('subscriptionId', 's.id'),
      ],
    },
    {
      table: 'partner_inquiries',
      propertyAlias: 'transferRequested',
      column: 'id',
      aggregate: 'COUNT',
      relations: [{
        path: 'subscription',
        alias: 'pis',
      }],
      whereCommands: [
        Equal('inquiryType', 'transfer'),
        Equal('status', 'requested'),
        Equal('subscriptionId', 's.id'),
      ],
    },
    {
      table: 'partner_inquiries',
      propertyAlias: 'transferApproved',
      column: 'id',
      aggregate: 'COUNT',
      relations: [{
        path: 'subscription',
        alias: 'pis',
      }],
      whereCommands: [
        Equal('inquiryType', 'transfer'),
        Equal('status', 'approved'),
        Equal('subscriptionId', 's.id'),
      ],
    },
    {
      table: 'partner_inquiries',
      propertyAlias: 'upgradeRequested',
      column: 'id',
      aggregate: 'COUNT',
      relations: [{
        path: 'subscription',
        alias: 'pis',
      }],
      whereCommands: [
        Equal('inquiryType', 'upgrade'),
        Equal('status', 'requested'),
        Equal('subscriptionId', 's.id'),
      ],
    },
    {
      table: 'partner_inquiries',
      propertyAlias: 'upgradeApproved',
      column: 'id',
      aggregate: 'COUNT',
      relations: [{
        path: 'subscription',
        alias: 'pis',
      }],
      whereCommands: [
        Equal('inquiryType', 'upgrade'),
        Equal('status', 'approved'),
        Equal('subscriptionId', 's.id'),
      ],
    },
    {
      table: 'partner_inquiries',
      propertyAlias: 'downgradeRequested',
      column: 'id',
      aggregate: 'COUNT',
      relations: [{
        path: 'subscription',
        alias: 'pis',
      }],
      whereCommands: [
        Equal('inquiryType', 'downgrade'),
        Equal('status', 'requested'),
        Equal('subscriptionId', 's.id'),
      ],
    },
    {
      table: 'partner_inquiries',
      propertyAlias: 'downgradeApproved',
      column: 'id',
      aggregate: 'COUNT',
      relations: [{
        path: 'subscription',
        alias: 'pis',
      }],
      whereCommands: [
        Equal('inquiryType', 'downgrade'),
        Equal('status', 'approved'),
        Equal('subscriptionId', 's.id'),
      ],
    },
  ],
  where: [
    Equal('se.name', 'Promoted Listings'),
    Equal('rejectionCount', 0),
    Equal('transferRejectionCount', 0),
  ],
  limit: 9999,
};

const defaultGetSingleFilters: IQueryCommandOptions = {};
const promotedListingGetSingleFilters: IQueryCommandOptions = {
  alias: 's',
  select: [
    'b.buildingName',
    'b.streetNumber',
    'b.streetName',
    'gc.cityName',
    'p.name',
    'p.partnerKey',
    'lplt.name',
    'lplt.tierLevel',
    'lplt.partnerKey',
    'lplt.description2',
    'lplt.feedLabel',
    's.id',
    's.startDate',
    's.minDuration',
    's.expiryDate',
    's.baseSubscriptionId',
    's.parentSubscriptionId',
    's.customConfiguration',
    'shb.activeDate',
    'rejectionCount',
    'transferRejectionCount',
    'cancellationRequested',
    'cancellationApproved',
    'transferRequested',
    'transferApproved',
    'upgradeRequested',
    'upgradeApproved',
    'downgradeRequested',
    'downgradeApproved',
  ],
  relations: [
    {
      path: 's.subscriptionsHasBuildings',
      alias: 'shb',
    },
    {
      path: 'shb.building',
      alias: 'b',
    },
    {
      path: 'b.city',
      alias: 'gc',
    },
    {
      path: 's.baseSubscription',
      alias: 'bs',
    },
    {
      path: 'bs.listingPromotedListingTypes',
      alias: 'lplt',
    },
    {
      path: 'bs.partner',
      alias: 'p',
    },
    {
      path: 'bs.service',
      alias: 'se',
    },
    {
      path: 'se.billingType',
      alias: 'bt',
    },
    {
      path: 's.partnerInquiries',
      alias: 'pi',
      whereCommands: [
        Equal('pi.inquiryType', 'cancellation'),
        In('pi.status', ['requested', 'approved']),
      ],
    },
    {
      path: 's.newPartnerInquiries',
      alias: 'npi',
      whereCommands: [
        In('npi.inquiryType', ['transfer', 'upgrade', 'downgrade']),
        In('npi.status', ['requested', 'approved']),
      ],
    },
  ],
  aggregations: [
    {
      table: 'partner_inquiries',
      propertyAlias: 'rejectionCount',
      column: 'id',
      aggregate: 'COUNT',
      relations: [{
        path: 'subscription',
        alias: 'pis',
      }],
      whereCommands: [
        Equal('inquiryType', 'promoted'),
        Equal('status', 'rejected'),
        Equal('subscriptionId', 's.id'),
      ],
    },
    {
      table: 'partner_inquiries',
      propertyAlias: 'transferRejectionCount',
      column: 'id',
      aggregate: 'COUNT',
      relations: [{
        path: 'subscription',
        alias: 'pis',
      }],
      whereCommands: [
        In('inquiryType', ['transfer', 'upgrade', 'downgrade']),
        Equal('status', 'rejected'),
        Equal('newSubscriptionId', 's.id'),
      ],
    },
    {
      table: 'partner_inquiries',
      propertyAlias: 'cancellationRequested',
      column: 'id',
      aggregate: 'COUNT',
      relations: [{
        path: 'subscription',
        alias: 'pis',
      }],
      whereCommands: [
        Equal('inquiryType', 'cancellation'),
        Equal('status', 'requested'),
        Equal('subscriptionId', 's.id'),
      ],
    },
    {
      table: 'partner_inquiries',
      propertyAlias: 'cancellationApproved',
      column: 'id',
      aggregate: 'COUNT',
      relations: [{
        path: 'subscription',
        alias: 'pis',
      }],
      whereCommands: [
        Equal('inquiryType', 'cancellation'),
        Equal('status', 'approved'),
        Equal('subscriptionId', 's.id'),
      ],
    },
    {
      table: 'partner_inquiries',
      propertyAlias: 'transferRequested',
      column: 'id',
      aggregate: 'COUNT',
      relations: [{
        path: 'subscription',
        alias: 'pis',
      }],
      whereCommands: [
        Equal('inquiryType', 'transfer'),
        Equal('status', 'requested'),
        Equal('newSubscriptionId', 's.id'),
      ],
    },
    {
      table: 'partner_inquiries',
      propertyAlias: 'transferApproved',
      column: 'id',
      aggregate: 'COUNT',
      relations: [{
        path: 'subscription',
        alias: 'pis',
      }],
      whereCommands: [
        Equal('inquiryType', 'transfer'),
        Equal('status', 'approved'),
        Equal('newSubscriptionId', 's.id'),
      ],
    },
    {
      table: 'partner_inquiries',
      propertyAlias: 'upgradeRequested',
      column: 'id',
      aggregate: 'COUNT',
      relations: [{
        path: 'subscription',
        alias: 'pis',
      }],
      whereCommands: [
        Equal('inquiryType', 'upgrade'),
        Equal('status', 'requested'),
        Equal('newSubscriptionId', 's.id'),
      ],
    },
    {
      table: 'partner_inquiries',
      propertyAlias: 'upgradeApproved',
      column: 'id',
      aggregate: 'COUNT',
      relations: [{
        path: 'subscription',
        alias: 'pis',
      }],
      whereCommands: [
        Equal('inquiryType', 'upgrade'),
        Equal('status', 'approved'),
        Equal('newSubscriptionId', 's.id'),
      ],
    },
    {
      table: 'partner_inquiries',
      propertyAlias: 'downgradeRequested',
      column: 'id',
      aggregate: 'COUNT',
      relations: [{
        path: 'subscription',
        alias: 'pis',
      }],
      whereCommands: [
        Equal('inquiryType', 'downgrade'),
        Equal('status', 'requested'),
        Equal('newSubscriptionId', 's.id'),
      ],
    },
    {
      table: 'partner_inquiries',
      propertyAlias: 'downgradeApproved',
      column: 'id',
      aggregate: 'COUNT',
      relations: [{
        path: 'subscription',
        alias: 'pis',
      }],
      whereCommands: [
        Equal('inquiryType', 'downgrade'),
        Equal('status', 'approved'),
        Equal('newSubscriptionId', 's.id'),
      ],
    },
  ],
  where: [
    Equal('se.name', 'Promoted Listings'),
    Equal('rejectionCount', 0),
    Equal('transferRejectionCount', 0),
  ],
};

// Instantiate the base store with the params
const base = new StoreBaseImplementationV2<IPromotedListing>({
  adminGatewayResourcePath,
  defaultGetSingleFilters,
  defaultGetListFilters,
  listViewName,
  showLogging,
});

interface IPromotedListingsState extends IStateStrict<IPromotedListing> {
  clientPromotedListingsBreakdown: IClientPromotedListingsBreakdown | null;
}

interface IPromotedListingsPayload {
  subscription: ISubscriptions;
  subscriptionsHasBuildings: ISubscriptionsHasBuildings;
  partnerInquiry: IPartnerInquiries;
}

interface IPromotedListingsTransferPayload {
  subscriptionId: number;
  propertyId: number;
  partnerInquiry: IPartnerInquiries;
  transferDate?: string;
}

// Extend StoreBaseImplementation object to add custom state/getters/mutations/actions.
export default {
  namespaced: base.namespaced, // 'promoted-listings/'

  state: {
    ...base.state,
    clientPromotedListingsBreakdown: null,
  },

  getters: {
    ...base.getters,
    clientPromotedListingsBreakdown(state: IPromotedListingsState) {
      return state?.clientPromotedListingsBreakdown;
    },

    formattedClientPromotedListingBreakdown(state: IPromotedListingsState): Record<string, number[]> {
      const initialBreakdown = state.clientPromotedListingsBreakdown || {};
      const finalBreakdown = Object.keys(initialBreakdown).reduce<Record<string, number[]>>((formattedBreakdown, partnerKey) => {
        const currentBreakdown = initialBreakdown[partnerKey];
        const partnerBreakdown = Object.keys(currentBreakdown)?.reduce<number[]>((propertyIds, baseSubId): number[] => {
          const contractsBreakdown = Object.keys(currentBreakdown[baseSubId])?.reduce<number[]>((idList, contractId): number[] => {
            const { propertyIds: contractPropertyIds = [], requestedPropertyIds = [] } = currentBreakdown[baseSubId][contractId] || {};
            return [...idList, ...contractPropertyIds, ...requestedPropertyIds];
          }, []);
          return [...propertyIds, ...contractsBreakdown];
        }, []);
        return {
          ...formattedBreakdown,
          [`${partnerKey}`]: [...partnerBreakdown],
        };
      }, {});
      return finalBreakdown;
    },

  },

  mutations: {
    ...base.mutations,
    setClientPromotedListingsBreakdown(ctx: IPromotedListingsState, payload: IClientPromotedListingsBreakdown) {
      ctx.clientPromotedListingsBreakdown = payload || {};
    },
  },

  actions: {
    ...base.actions,

    getPromotedListings: async (ctx: IAction<IPromotedListingsState>, payload?: IFilterPayloadV2) => {
      const queryCommands = mergeQueryCommandOptions([promotedListingGetListFilters, payload?.filters ?? {}]);
      const queryString = convertQueryCommandToQueryString(queryCommands);
      const viewParam = `&view=${listViewName}`;
      const result = await adminRequest.get(`${adminGatewayResourcePath}`, `${queryString + viewParam}`);
      ctx.commit('set', result?.data ?? []);
    },

    getSinglePromotedListing: async (ctx: IAction<IPromotedListingsState>, id: number, payload?: IFilterPayloadV2) => {
      const queryCommands = mergeQueryCommandOptions([promotedListingGetSingleFilters, payload?.filters ?? {}]);
      const queryString = convertQueryCommandToQueryString(queryCommands);
      const viewParam = `&view=${listViewName}`;
      const result = await adminRequest.get(`${adminGatewayResourcePath}/${id}`, `${queryString + viewParam}`);
      ctx.commit('set', result?.data ?? {});
    },

    getPromotedListingsCSV: async (ctx: IAction<IPromotedListingsState>, payload?: IExportFilterPayloadV2) => {
      const queryCommands = mergeQueryCommandOptions([promotedListingGetListFilters, payload?.filters ?? {}]);
      const queryString = convertQueryCommandToQueryString(queryCommands);
      const viewParam = `&view=${listViewName}`;
      const downloadParam = `&csvExport=true`;
      const result = await adminRequest.post(`${adminGatewayResourcePath}${queryString + viewParam + downloadParam}`, { fields: payload?.fields });
      if (payload?.doDownload) {
        downloadCSV(typeof result === 'string' ? result : '', payload.filename || `${dayjs().format('DD-MM-YYYY')}_report.csv`);
      }
      return result;
    },

    fetchClientPromotedListingsBreakdown: async (ctx: IAction<IPromotedListingsState>, clientId: number) => {
      const result = await adminRequest.get(`/syndication-api/client_promoted_listings_breakdown/${clientId}`);
      ctx.commit('setClientPromotedListingsBreakdown', result?.data);
    },

    addPromotedListings: async (ctx: IAction<IPromotedListingsState>, promotedListings: IPromotedListingsPayload[]) => {
      const result = await adminRequest.post(
        '/syndication-api/promoted_listings',
        promotedListings,
      );
      return result;
    },

    transferPromotedListing: async (ctx: IAction<IPromotedListingsState>, promotedListings: IPromotedListingsTransferPayload) => {
      const result = await adminRequest.post(
        '/syndication-api/promoted_listing_transfers',
        promotedListings,
      ) as IApiResponse<IPromotedListingTransferResponse, any>;
      const partnerInquiries = result.data?.insertedRecords.partnerInquiries;
      ctx.commit('partner-inquiries/appendToList', partnerInquiries, { root: true });
      return result;
    },
  },

};
